
import React from "react";
import { i18nManager } from "../../../../i18n";
import * as ReactRouterDom from "react-router-dom";
import { Schemes, Utils } from "../../../../utils";
import { Elements } from "../../../shared/Elements";
import AnalCard from "../../../shared/analytics/AnalCard";
import { ChartManager } from "../../../shared/ChartManager";
import { ChartData, ChartType, Classname, Column, DateHelper, DateTimeInput, DateTimePickerLayout, DateTimePickerSelectionMode, Dropdown, FormControl, StringHelper, Table, TextInput } from "@rivtn/noseur";
import { ApiResponseData, BaseService, EarningService, IMember, TransactionFlow, HostService, IHost, IHostTransaction, IHostAnalytics, SanitizedResponse, IHostManager, IHostManagerEarning, HostRole, IHostTournamentAnalytics } from "../../../../services";

export interface HostEarningAnalyticsProps {
    profile: IMember;
}

function HostEarningAnalytics(props: HostEarningAnalyticsProps) {

    const { profile } = props;
    const hostService = HostService.getInstance();
    const labels = i18nManager.Labels.host.analytics;
    let { id: hostId = "" } = ReactRouterDom.useParams();
    const [host, setHost] = React.useState<IHost>({} as any);
    const hostAnalyticsChartDataState = React.useState<ChartData>();
    const [daysDifference, setDaysDifference] = React.useState<number>(0);
    const [hostAnalytics, setHostAnalytics] = React.useState<IHostAnalytics>();
    const [selectedHostManagers, setSelectedHostManagers] = React.useState<IHostManager[]>([]);
    const [hostManagerEarnings, setHostManagerEarnings] = React.useState<IHostManagerEarning[]>();
    const [searchParams, setSearchParams] = ReactRouterDom.useSearchParams(window.location.search);
    const [hostTransactions, setHostTransactions] = React.useState<ApiResponseData<IHostTransaction>>({});
    const currencySign = i18nManager.getCurrencySign(searchParams.get("curr") ?? "NGN");

    React.useEffect(fetchInformation, []);

    React.useEffect(() => {
        setDaysDifference(DateHelper.getDaysBetweenDates(
            (searchParams.has("from") ? new Date(searchParams.get("from")!) : DateHelper.getStartOfMonth()),
            (searchParams.has("to") ? new Date(searchParams.get("to")!) : new Date())));
    }, [searchParams]);

    return (<div className="earnings analytics">
        <div className="header">
            <div className="l" style={{ display: "flex", flexDirection: "column" }}>
                <span className="t lined-limit"><i className="fa fa-arrow-left" onClick={() => window.history.back()} />
                    {i18nManager.Labels.host.title + " - " + hostId + " - " + labels.title}
                </span>
                <span className="lined-limit" style={{ marginLeft: 25, fontWeight: "normal" }}>
                    {host.name}{" "}
                    {!!selectedHostManagers.length && (<>
                        - {i18nManager.Labels.teams.analytics.earnings.earning_anals_for} {selectedHostManagers.map((s) => s.member?.username).join(", ")}
                    </>)}
                </span>
            </div>
            <div className="queries">
                <Dropdown noIcon scheme={Schemes.OMNEHOST} formControlProps={{ className: "plan-dd", isFieldset: true, label: i18nManager.Labels.teams.analytics.country_currency, }}
                    selectedOptionIndex={(i18nManager.CountriesAndCurrencies?.findIndex((p) => p?.currency_code === (searchParams.get("curr") ?? "NGN")) ?? 0)}
                    textInputProps={{ style: { paddingLeft: 15 } }}
                    options={i18nManager.CountriesAndCurrencies} optionMap={{
                        value: "currency_code",
                        label: "{label} ({currency_name} - {currency_code})",
                    }} onSelectOption={(option) => {
                        Utils.updateSearchParams("curr", option.currency_code, setSearchParams, fetchInformation);
                    }} />
                <FormControl className="date-range-dd" isFieldset scheme={Schemes.OMNEHOST} label={i18nManager.Labels.teams.analytics.date_range} rightContent={"fa fa-calendar"} style={{ width: 360 }}>
                    <DateTimeInput fill leftLayout={DateTimePickerLayout.FINE_LEFT_LAYOUT} formatDate={(date) => Utils.formatToFineDate4(date)}
                        selectedDates={[(searchParams.has("from") ? new Date(searchParams.get("from")!) : DateHelper.getStartOfMonth()), new Date(searchParams.get("to") ?? Utils.formatToApiDate(new Date(), false, false)!)]}
                        selectionMode={DateTimePickerSelectionMode.RANGE} onSelectDate={(options) => {
                            Utils.updateSearchParamses({
                                "to": Utils.formatToApiDate(options.toDate!, false, false),
                                "from": Utils.formatToApiDate(options.fromDate!, false, false),
                            }, setSearchParams, fetchInformation);
                        }} />
                </FormControl>
            </div>
        </div>
        <div className="container" style={{ flexDirection: "column" }}>
            <div className="primary" style={{ flex: "unset" }}>
                <FormControl isFieldset fill label={labels.tournaments}>
                    <TextInput />
                </FormControl>
                <div className="anal-cards" style={{ marginTop: 15 }}>
                    <AnalCard anal={hostAnalytics?.earnings?.total} currencySign={currencySign} desc={labels.earned_within_x_days} title={labels.total_earnings}
                        icon="fas fa-sack-dollar" daysDifference={daysDifference} />
                    <AnalCard anal={hostAnalytics?.earnings?.tournament} currencySign={currencySign} desc={labels.earnings.earned_from_tours_regs} title={labels.earnings.tournament_earnings}
                        icon="fas fa-sack-dollar" />
                    <AnalCard anal={hostAnalytics?.earnings?.merchandise} currencySign={currencySign} desc={labels.earnings.earned_from_merchs} title={labels.earnings.merch_earnings}
                        icon="fas fa-sack-dollar" />
                    <AnalCard anal={hostAnalytics?.earnings?.advertisement} currencySign={currencySign} desc={labels.earnings.earned_from_rivtn_ads} title={labels.earnings.ads_earnings}
                        icon="fas fa-sack-dollar" />
                    <AnalCard anal={hostAnalytics?.earnings?.total} desc={labels.earnings.transactions.toLowerCase()}
                        daysDifference={daysDifference} analKey="count" style={{ maxWidth: 150 }} />
                </div>
            </div>
            <div className="analytics" style={{ flex: 1 }}>
                <div className="container" style={{ marginTop: 0 }}>
                    <div className="primary" style={{ marginTop: -10 }}>
                        {<ChartManager type={ChartType.BAR} title={labels.host_anals} chartDataState={hostAnalyticsChartDataState} />}
                        <div className="anal-table">
                            <div className="header">
                                <span className="t">{labels.transactions.last_10_earnings}</span>
                                <ReactRouterDom.NavLink to={{ pathname: `/hosts/${hostId}/analytics/transactions`, search: window.location.search }}>{labels.transactions.view_all_trans}</ReactRouterDom.NavLink>
                            </div>
                            <Table stripedRows scheme={Schemes.OMNEHOST} data={hostTransactions.content} totalRecords={hostTransactions.total_elements} dataRefreshKeys={["external_id", "created_at"]}
                                style={{ flex: 1 }} rowsPerPage={200} loadingState={Elements.LOADING_STATE} emptyState={Elements.buildEmptyListView(undefined, (<span>
                                    {labels.transactions.empty_label_parts.no_earns_found}{" "}
                                    <ReactRouterDom.NavLink to={`"/hosts/create-tournament"`}>{labels.transactions.empty_label_parts.create}</ReactRouterDom.NavLink>{" "}
                                    {labels.transactions.empty_label_parts.a_tours}{" "}
                                    <ReactRouterDom.NavLink to={`/hosts/${hostId}/merch`}>{labels.transactions.empty_label_parts.create_merch}</ReactRouterDom.NavLink>{" "}
                                    {labels.transactions.empty_label_parts.to_gen_income}
                                </span>), { marginTop: 50 })}>
                                <Column className="table-c" header={labels.transactions.table.headers.date} dataKey="date" template={(v) => Utils.formatToFineDate(v, true)} />
                                <Column className="table-c" header={labels.transactions.table.headers.amount} dataKey="amount" template={(v: number) => (`${currencySign} ${StringHelper.toPresentableMoneyValue(v)}`)} />
                                <Column className="table-c" header={labels.transactions.table.headers.source} template={(v: IHostTransaction) => {
                                    return "TODO";
                                    /*let link = EarningService.buildTransactionSourceLink(v);
                                    const label = v.source?.label ?? v.source?.extra_data?.name;
                                    return (<div style={{ display: "flex", flexDirection: "row", alignItems: "center", }}>
                                        {v.source?.extra_data?.logo && (<img alt={label} src={v.source?.extra_data?.logo} style={{ width: 20, height: 20, marginRight: 10, borderRadius: "100%" }} />)}
                                        {link ? (<a target="_blank" href={link}>{label}</a>) : (<span>{label}</span>)}
                                    </div>);*/
                                }} />
                                <Column className="table-c" header={labels.transactions.table.headers.recipient} template={(v: IHostTransaction) => ((v.recipient as IHostManager).member?.username)} />
                            </Table>
                        </div>
                    </div>
                    <div className="secondary" style={{ marginTop: 5 }}>
                        <div className="tt-earners">
                            <span className="t">{labels.top_earning_managers}</span>
                            {!hostManagerEarnings && Elements.LOADING_STATE}
                            <div className="listers">
                                {hostManagerEarnings?.map((hostManagerEarning) => {
                                    const isOwner = hostManagerEarning.host_manager.role === HostRole.OWNER;
                                    const isYou = hostManagerEarning.host_manager.member?.external_id === profile.external_id && (labels.earnings.you);
                                    const memberIndex = selectedHostManagers.findIndex((s) => s.external_id === hostManagerEarning.host_manager.external_id);

                                    return (<div key={hostManagerEarning.host_manager.external_id} className={Classname.build("lister", { "active": (memberIndex > -1) })} onClick={() => {
                                        if (memberIndex > -1) {
                                            selectedHostManagers.splice(memberIndex, 1);
                                        } else {
                                            selectedHostManagers.push(hostManagerEarning.host_manager);
                                        }
                                        setSelectedHostManagers([...selectedHostManagers]);
                                    }}>
                                        <img className="bg" alt={hostManagerEarning.host_manager.member?.username} src={hostManagerEarning.host_manager.member?.profile_banner_url} />
                                        <img className="lo" alt={hostManagerEarning.host_manager.member?.username} src={hostManagerEarning.host_manager.passport_picture_url} />
                                        <div className="details">
                                            <span className="m">{currencySign} {StringHelper.toPresentableMoneyValue(hostManagerEarning.amount)}</span>
                                            <span>
                                                {hostManagerEarning.host_manager.member?.username}{" "}
                                                {(isYou || isOwner) && `(${isOwner ? labels.earnings.owner + (isYou ? ", " : "") : ""}${isYou ? labels.earnings.you : ""})`}
                                            </span>
                                        </div>
                                        <i className="fa fa-circle" style={{
                                            color: ((memberIndex > -1) ? "var(--mainColor)" : "transparent"),
                                            padding: 1, borderRadius: "100%", border: "1px solid var(--mainColor)",
                                        }} />
                                    </div>);
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>);

    function fetchInformation() {
        hostService.getHostByPublicId(hostId!).then(({ sanitized }: SanitizedResponse<IHost>) => {
            setHost(sanitized);
        }).catch(BaseService.reportError);

        const params = {
            currency_code: searchParams.get("curr") ?? "NGN",
            to: searchParams.get("to") ?? Utils.formatToApiDate(new Date())!,
            fields: ["earnings.total", "tournaments.entered", "achievements.total"],
            from: searchParams.get("from") ?? Utils.formatToApiDate(DateHelper.getStartOfMonth())!,
        };
        setHostAnalytics(undefined);
        hostService.getHostAnalytics(hostId, params).then(({ sanitized }: SanitizedResponse<IHostAnalytics>) => {
            setHostAnalytics(sanitized);
        }).catch(BaseService.reportError);

        setHostManagerEarnings(undefined);
        hostService.getHostManagerEarnings(hostId, params).then(({ content }: SanitizedResponse<IHostManagerEarning>) => {
            setHostManagerEarnings(content);
        }).catch(BaseService.reportError);

        hostAnalyticsChartDataState[1](undefined);
        hostService.queryAnalyticsChartData(hostId, params).then(({ apiResponse }: SanitizedResponse<any>) => {
            hostAnalyticsChartDataState[1](apiResponse.data!);
        }).catch(BaseService.reportError);

        setHostTransactions({});
        hostService.queryTransactions(hostId, { ...params, size: 10, flow: TransactionFlow.OUT }).then(({ apiResponse }: SanitizedResponse<any>) => {
            setHostTransactions(apiResponse.data!);
        }).catch(BaseService.reportError);
    }

}

export default HostEarningAnalytics;
